import AlertInline from '@storeblocks/inline-alert';
import { LoadingIndicatorSpinner } from '@storeblocks/spinner-loading-indicator';
import { type FC, useState } from 'react';
import styled from 'styled-components';

import { useOrganizationUsersQuery } from '@/api/organizationUsers/organizationUsersApi';
import { Show } from '@/components/Show';
import { useTexts } from '@/hooks/useTexts';

import { AddOrganizationUsersModal } from './AddAndEditUserModal/AddOrganizationUsers';
import { OrganizationActiveUsersTable } from './OrganizationActiveUsersTable';
import { OrganizationInvitedUsersTable } from './OrganizationInvitedUsersTable';
import { Toolbar } from './Toolbar';
import { useUserSearch } from './useUserSearch';

export const OrganizationUserAdmin: FC = () => {
  const texts = useTexts();

  const [searchValue, setSearchValue] = useState<string | undefined>();

  const organizationUsersQuery = useOrganizationUsersQuery();

  const { activeUsers, inactiveUsers } = useUserSearch(
    searchValue,
    organizationUsersQuery.data,
  );

  if (organizationUsersQuery.isError) {
    return (
      <Wrapper>
        <AlertInline variant="error" title={texts.admin.user.loadError} />
      </Wrapper>
    );
  }

  if (organizationUsersQuery.isLoading) {
    return (
      <SpinnerWrapper>
        <LoadingIndicatorSpinner />
      </SpinnerWrapper>
    );
  }

  return (
    <Wrapper>
      <Toolbar
        onSearchParamsChange={(params) => setSearchValue(params.query)}
        modal={AddOrganizationUsersModal}
      />

      <Show when={inactiveUsers.length > 0}>
        <Label>{texts.admin.user.inactiveUsers}</Label>
        <OrganizationInvitedUsersTable organizationUsers={inactiveUsers} />
      </Show>

      <Show when={activeUsers.length > 0}>
        {/* Only show label if the table above is visible. */}
        <Show when={inactiveUsers.length > 0}>
          <Label>{texts.admin.user.activeUsers}</Label>
        </Show>

        <OrganizationActiveUsersTable organizationUsers={activeUsers} />
      </Show>

      <Show when={activeUsers.length === 0 && inactiveUsers.length === 0}>
        <AlertInline title={texts.admin.users.none} />
      </Show>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  margin-top: 32px;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Label = styled.div`
  margin-top: 32px;
  font-weight: bold;
`;

const SpinnerWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 32px;
`;
